import React from 'react'
import { MustacheSkeletorRenderer } from './skeletor-renderer'
import { MustacheRendererProps } from './shared'

export type MustacheClickHandler = (...args: any[]) => void
interface MustacheWithClickHandlerProps extends MustacheRendererProps {
  clickMethodTarget: string
  clickHandler: MustacheClickHandler
  testId: string
}

/*
  This component is used for mustache templates with the clickMethod tag.
  This component will search for a data-click-method matching the value in the clickMethod tag
  and add the provided click handler, calling it with any params provided with the clickMethod
  Example:
  <MustachWithClickHandler
    clickMethodTarget="DOWNLOAD_ASYNC_REPORT"
    clickHandler={downloadAttestationReport}
    tagName="span"
    template={mustacheTemplate}
  />
*/

export function MustacheWithClickHandler({
  clickMethodTarget,
  clickHandler,
  tagName,
  template,
  views = {},
  testId
}: MustacheWithClickHandlerProps) {
  const ref = React.useRef<HTMLSpanElement>(null)

  const handleClick = React.useCallback(
    (e: Event) => {
      e.stopImmediatePropagation()
      if (e.target) {
        const params = (e.target as HTMLElement).getAttribute(
          'data-click-method-params'
        )
        const paramsList = params ? params.split(',') : []
        clickHandler(...paramsList)
      }
    },
    [clickHandler]
  )

  React.useEffect(() => {
    if (ref && ref.current) {
      const element = ref.current.querySelector(
        `[data-click-method="${clickMethodTarget}"]`
      )
      if (element) {
        element.addEventListener('click', handleClick)
        return () => {
          element.removeEventListener('click', handleClick)
        }
      }
    }
  }, [ref, clickMethodTarget, handleClick])
  return (
    <span ref={ref}>
      <MustacheSkeletorRenderer
        testId={testId}
        textType="body"
        tagName={tagName}
        template={template}
        views={views}
      />
    </span>
  )
}
